import AppModal from '@common/ui/AppModal';
import { Loader, MaterialIcon } from '@components';
import { useGlobalNotifier } from '@hooks';
import { useGetSavedQuery, useUpdateSavedQuery, useCanDeleteSavedQuery } from '@Inventory/services/savedQueryQueries';
import { LoadingButton } from '@mui/lab';
import { Stack, Typography, Button, styled, Box } from '@mui/material';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InventoryPageQueryKeys } from '@Inventory/common/queryKeys';
import { AccessFlowsLinkedToSavedQueryAppModel, TriggerTypeAppModel } from '@api';
import { TriggerTypeClickableChip } from '@common/organisms/TriggerTypeClickableChip/TriggerTypeClickableChip';

const AccessFlowRowContainer = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1),
}));

export function EditInventoryQueryModal({ open }: { open: boolean }) {
  const [queryParams, setQueryParams] = useSearchParams();

  const onClose = () => {
    const newQueryParams = new URLSearchParams(queryParams);
    newQueryParams.delete(InventoryPageQueryKeys.EditQueryModalOpen);
    setQueryParams(newQueryParams);
  };

  const query = queryParams.get(InventoryPageQueryKeys.Query);
  const ModalHeader = (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
      <MaterialIcon symbol="warning" weight={700} size="medium" color="error" />
      <Typography variant="subtitle1">Update Access Scope</Typography>
    </Stack>
  );

  const id = queryParams.get(InventoryPageQueryKeys.SavedQueryId);
  if (!id || !query) {
    return null;
  }

  return (
    <AppModal open={open} onClose={onClose} header={ModalHeader} width={800}>
      <EditInventoryQueryModalInner onClose={onClose} query={query} id={id} />
    </AppModal>
  );
}

function EditInventoryQueryModalInner({ onClose, query, id }: { onClose: () => void; query: string; id: string }) {
  const { savedQuery, isSavedQueryFetched } = useGetSavedQuery(id);

  const { canDeleteSavedQuery, isCanDeleteSavedQueryLoading } = useCanDeleteSavedQuery(id);

  const { updateSavedQuery, isUpdateSavedQueryLoading, updatedSavedQuery } = useUpdateSavedQuery(id);

  const { notifySuccess } = useGlobalNotifier();

  useEffect(() => {
    if (updatedSavedQuery) {
      notifySuccess(`Updated access scope ${savedQuery?.name} successfully`);
      onClose();
    }
  }, [updatedSavedQuery, notifySuccess, onClose, savedQuery?.name]);

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" spacing={0.25}>
        <Typography variant="body2" color={'text.muted'}>
          Be aware that updating Access Scope: &quot;
        </Typography>
        <Typography variant="body2">{savedQuery?.name}</Typography>
        <Typography variant="body2" color="text.muted">
          &quot; will affect Access Flows and the requests users can make.
        </Typography>
      </Stack>
      <Stack gap={1} sx={{ overflow: 'auto', scrollbarWidth: 'none' }}>
        {(!isSavedQueryFetched || isCanDeleteSavedQueryLoading) && <Loader absolute={false} padding={2} />}

        {!canDeleteSavedQuery?.can_delete && canDeleteSavedQuery?.access_flows_used && (
          <>
            <Stack direction="row" spacing={2} justifyContent={'space-between'}>
              <Typography variant="subtitle2">This Access Scope is used in these Access Flows:</Typography>
              <Typography variant="helperText" color="text.muted">
                {canDeleteSavedQuery?.access_flows_used.length} Access Flows
              </Typography>
            </Stack>
            {canDeleteSavedQuery?.access_flows_used.map((accessFlow) => (
              <AccessFlowRow accessFlow={accessFlow} key={accessFlow.id} />
            ))}
          </>
        )}
      </Stack>
      <Stack direction={'row'} justifyContent="space-between" spacing={2}>
        <Button variant="outlined" onClick={onClose}>
          Go Back
        </Button>
        {savedQuery && (
          <LoadingButton
            onClick={() => updateSavedQuery({ ...savedQuery, query: query || '' })}
            loading={isUpdateSavedQueryLoading}
            variant="contained"
            color="primary"
            startIcon={<MaterialIcon symbol="save" />}
          >
            Save Changes
          </LoadingButton>
        )}
      </Stack>
    </Stack>
  );
}

function AccessFlowRow({ accessFlow }: { accessFlow: AccessFlowsLinkedToSavedQueryAppModel }) {
  const triggerType = getTriggerType(accessFlow.trigger_type);
  return (
    <AccessFlowRowContainer>
      {triggerType && <TriggerTypeClickableChip triggerType={triggerType} />}
      <Typography variant="subtitle2">{accessFlow.name}</Typography>
    </AccessFlowRowContainer>
  );
}

function getTriggerType(trigger: string) {
  const entries = Object.entries(TriggerTypeAppModel);
  for (const [key, value] of entries) {
    if (value === trigger) {
      return value;
    }
  }
  return null;
}
